import React from 'react';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {doSelectedItemsExceedLimit} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import {invalidateInDashboard} from 'processes/ProcessManagementActions';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskListingGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import * as ProcessManagementDAO from '../dao/ProcessManagementDAO';
import {statusTypeToDisplayName} from '../ProcessConstants';

interface UndoExecutionButtonProps {
  tasks: Array<ProcessTaskListingGraphQL>;
}

function getUndoExecutionErrorMessage(tasks: Array<ProcessTaskListingGraphQL>): string | null {
  if (tasks.length === 0) {
    return 'No tasks selected to undo execution';
  } else if (tasks.some((task) => task.status.statusType !== ProcessTaskStatusType.IN_PROGRESS)) {
    return `All selected tasks must be ${statusTypeToDisplayName(
      ProcessTaskStatusType.IN_PROGRESS
    )!.toLocaleLowerCase()} to undo execution`;
  } else if (tasks.some((task) => task.schema.id !== tasks[0].schema.id)) {
    return 'All selected tasks must have the same schema to undo execution';
  } else if (tasks.some((task) => task.executionOrigin == null)) {
    return 'All selected tasks must be previously executed to undo execution';
  } else if (
    tasks[0].schema.entryTemplate != null &&
    tasks.some(
      (task) => task.executionOrigin!.executionEntry!.id !== tasks[0].executionOrigin!.executionEntry!.id
    )
  ) {
    return 'All selected tasks must be executed into the same entry to undo execution';
  }

  return null;
}

export default function UndoExecutionButton(props: UndoExecutionButtonProps): JSX.Element {
  const disabledTooltip = getUndoExecutionErrorMessage(props.tasks);
  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.tasks);

  return (
    <TooltipWrapper tooltipTitle={isExceedingLimitMessage ?? disabledTooltip ?? 'Undo execution'}>
      <Button
        disabled={isExceedingLimit || disabledTooltip != null}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        icon="undo"
        onClick={async () => {
          const undoExecution =
            props.tasks[0].schema.entryTemplate != null
              ? ProcessManagementDAO.undoEntryExecution
              : ProcessManagementDAO.undoDirectExecution;

          await withLoadingScreen(
            (async () => {
              const {tasks} = await undoExecution({
                taskIds: props.tasks.map(({id}) => id),
              });
              invalidateInDashboard(tasks);
              notifications.success('Cancelled task execution');
            })(),
            'Cancelling task execution...'
          );
        }}
        dataTestAttrs={{component: 'ProcessManagementToolbar', element: 'UndoExecutionButton'}}
      />
    </TooltipWrapper>
  );
}
